<template>
  <overlay-loader v-if="isGeneratingImage" />
  <div
    v-bind="$attrs"
    :id="anchor"
    ref="container"
    class="annotatable-richtext"
    :class="wrapperCssClass"
  >
    <rich-text
      :class="cssClass"
      :content="content"
      :lazyload="lazyload"
      :expandable="expandable"
      :expanded="expanded"
      :annotatable="annotatable"
      :can-mark-copy="canMarkCopy"
      :config="config"
      :truncation-type="truncationType"
      :is-own-view="isOwnView"
      :is-student-view="isStudentView"
      :component-type="componentType"
      :show-drawing-button="showDrawingButton"
      :has-s-e-audio="hasSEAudio"
      :is-s-e-graded="isSEGraded"
      :show-component-overlay="showComponentOverlay"
      :question-attempt-uuid="questionAttemptUuid"
      :enable-annotation-scroll="enableAnnotationScroll"
      :show-drawing-restriction="showDrawingRestriction"
      :anchor="anchor"
      :visible="visible"
      :copyright-protected="copyrightProtected"
      :is-comment="isComment"
      :truncate="truncate"
      :comment-context="commentContext"
      :can-ask-salis="canAskSalis"
      :is-ask-salis-supported-component="isAskSalisSupportedComponent"
      @is-mode-changed="isModeChanged"
      @click-image="clickImage"
      @double-click-image="doubleClickImage"
      @timestamp-trigger="timestampTrigger"
      @ready="onRichTextReady"
      @mark-google-file="handleMarkGoogleFile"
      @handle-anchor-annotation="handleAnchorAnnotation"
      @handle-make-image-copy="handleMakeImageCopy"
      @mark-copy="handleMarkCopy"
    />
    <div :class="textAnnotationWrapperClass" />
  </div>
</template>

<script setup lang="ts">
import {
  ComponentPublicInstance,
  computed,
  createApp,
  getCurrentInstance,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useStore } from "vuex";
import DOMUtils from "@utils/dom.utils";
import {
  ANCHORING_CONTEXT_TYPE,
  ANNOTATION_CONTEXT_TYPE,
  ANNOTATION_MODE,
  ANNOTATION_TYPE,
  NAMESPACE,
} from "@modules/annotation/service/annotation.constant";
import AccordionComponentStore from "@plugins/accordion/components/AccordionComponentStore";
import { usePageDrawer } from "@modules/app/composables/page.drawer.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import { AnchoringEventBus } from "@/modules/annotation/service/anchoring.event.bus";
import { SpeechEvaluationEventBus } from "@plugins/richtext/plugins/speech-evaluation/service/event-bus";
import { useAnnotationAnchor } from "@modules/annotation/composables/annotation.anchor.ts";
import { useAnnotationNote } from "@modules/annotation/composables/annotation.note.ts";
import { useAnnotation } from "@modules/annotation/composables/annotation.ts";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import { useVm } from "@utils/vm.ts";
import { useLessonOwnViewMentions } from "@modules/attempt/composables/use-lesson-own-view-mentions.ts";
import TextAnnotation from "./TextAnnotation.vue";
import ImageAnnotation from "./ImageAnnotation.vue";
import TimestampAnnotation from "./TimestampAnnotation.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import { useDynamicMountParent } from "@plugins/common/composables/dynamic-mount-parent.ts";
import randomUtils from "@utils/random.utils.ts";
import OverlayLoader from "@plugins/common/components/OverlayLoader.vue";
import drawingEventBus from "@plugins/richtext/plugins/drawing/service/event-bus";
import { AnnotationContext } from "@modules/annotation/types.ts";
import { CanvasConfig } from "@plugins/richtext/types.ts";
import { MentionableListProvider } from "@plugins/richtext/plugins/mentions/types.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useI18n } from "vue-i18n";
import { useAttemptMention } from "@modules/attempt/composables/use-attempt-mention.ts";
import { useRichTextImageExport } from "@modules/annotation/composables/use-rich-text-image-export.ts";

interface Props {
  anchor?: string;
  resourceType?: string;
  content?: string;
  expandable?: boolean;
  expanded?: boolean;
  annotationResourceId?: string;
  config?: object;
  canAnnotate?: boolean;
  canAssess?: boolean;
  truncationType?: string;
  ownerType?: string;
  responseSizeHeight?: string;
  componentType?: string;
  isResultMode?: boolean;
  canMarkCopy?: boolean;
  hasSEAudio?: boolean;
  isSEGraded?: boolean;
  isSERichText?: boolean;
  parentAnchor?: string | null;
  parentQuestionContext?: unknown[];
  showComponentOverlay?: boolean;
  // Kept as questionAttemptUuid (not renamed to a generic name) because this
  // value is forwarded unchanged into RichText.vue -> MediaComponent.vue,
  // which use the same key throughout (including the "newFileDuration" event
  // payload MediaComponent emits for AudioResponseAttempt/
  // AudioResponseTeamAttempt to consume). Renaming it here without also
  // renaming it through those shared plugin components would only introduce
  // a naming mismatch, not real decoupling.
  // TODO: revisit as part of a dedicated RichText/MediaComponent cleanup.
  questionAttemptUuid?: string;
  showDrawingRestriction?: boolean;
  visible?: boolean;
  copyrightProtected?: boolean;
  isComment?: boolean;
  truncate?: boolean;
  commentContext?: Record<string, unknown> | null;
  allowAskSalis?: boolean;
  canAskSalis?: boolean;
  isAskSalisSupportedComponent?: boolean;
}

interface ImageAnnotationPublic extends ComponentPublicInstance {
  handleImgZoomOrReset?: (params: unknown) => void;
  handleClick?: (params: unknown) => void;
  isModeChanged?: (params: unknown) => void;
}
interface TimestampAnnotationPublic extends ComponentPublicInstance {
  handleTrigger?: (params: { index: string }) => void;
}
interface TextAnnotationPublic extends ComponentPublicInstance {
  handleSelectionChange?: (isPreviousComponent: boolean) => void;
}

const props = withDefaults(defineProps<Props>(), {
  anchor: "",
  resourceType: "",
  content: "",
  expandable: true,
  expanded: true,
  annotationResourceId: "",
  config: () => ({}),
  canAnnotate: false,
  canAssess: false,
  truncationType: "primary",
  ownerType: "USER",
  responseSizeHeight: "",
  componentType: "",
  isResultMode: false,
  canMarkCopy: false,
  hasSEAudio: false,
  isSEGraded: false,
  isSERichText: false,
  parentAnchor: null,
  parentQuestionContext: () => [],
  showComponentOverlay: false,
  questionAttemptUuid: "",
  showDrawingRestriction: false,
  visible: true,
  copyrightProtected: false,
  isComment: false,
  truncate: false,
  commentContext: undefined,
  allowAskSalis: false,
  canAskSalis: false,
  isAskSalisSupportedComponent: false,
});

const emit = defineEmits<{
  markGoogleFile: [data: unknown];
  richTextReady: [];
  markCopy: [data: unknown];
  markImage: [data: CanvasConfig];
}>();

const componentContext = inject("componentContext");
const annotationContext = inject<AnnotationContext>("annotationContext")!;
const richTextContext = inject("richTextContext");
let questionContext = inject("questionContext");

// Methods/computed/data that are not found:
// - handleError

const store = useStore();
const eventBusWatch = useEventBusWatch();
const { vm, vmExpose } = useVm();
const { dynamicMountParentMixin_childComponents } = useDynamicMountParent();
const annotationAdapter = annotationContext.adapter;
const { canAccessAnnotations, canCreateAnnotations } = useAnnotationPermissions();
const { handleCollapseDrawer } = usePageDrawer({
  drawerName: annotationContext.drawerName,
});
const { handleAnchor } = useAnnotationAnchor();
const { getLessonOwnViewMentionableTeacherList, getLessonOwnViewMentionableStudentList } =
  useLessonOwnViewMentions();
const { getAttemptMentionableTeacherList, getAttemptMentionableStudentList } = useAttemptMention();
const { isEditorContext } = useAnnotationPermissions();
const { exportRichTextToImage } = useRichTextImageExport();

const getMentionableTeacherList: MentionableListProvider = () => {
  if (isEditorContext.value) {
    return getLessonOwnViewMentionableTeacherList();
  }
  return getAttemptMentionableTeacherList();
};

const getMentionableStudentList: MentionableListProvider = (includeBuddyList = false) => {
  if (isEditorContext.value) {
    return getLessonOwnViewMentionableStudentList();
  }
  return getAttemptMentionableStudentList(includeBuddyList);
};

const { listLoaded, isStudentView, isOwnView } = useAnnotation(undefined, annotationAdapter);
const { handleEditNotes } = useAnnotationNote(
  {
    deactiveNoteToggle: undefined,
    handleNoteChangeConfirm: undefined,
    handleNoteChangeCancel: undefined,
    getContext: undefined,
    getMentionableTeacherList,
    getMentionableStudentList,
    mode: "",
  },
  null,
);
const toast = useToast();
const { t } = useI18n();

const instance = getCurrentInstance()!;

const container = useTemplateRef("container");
const richTextReady = ref<boolean>(false);
const lazyload = ref<boolean>(false);
const selectedAnchor = ref<string>("");
const accordionStore = ref(AccordionComponentStore);
const childRichTextReady = ref<boolean>(false);
const txtAnnotation = ref<TextAnnotationPublic | null>(null);
const imgAnnotations = ref<ImageAnnotationPublic[]>([]);
const timestampAnnotations = ref<Map<string, TimestampAnnotationPublic>>(new Map());
// The ".text-annotation-wrapper.<anchor>" mount target's class binds to props.anchor, and the watchers
// that call loadAnnotation() run before the DOM re-patches - so a late-resolving anchor can make
// the query miss once. Each miss schedules a nextTick retry; this flag caps concurrent triggers
// to one pending retry, since parallel retries would double-mount the annotation apps.
const isTextAnnotationMountRetryPending = ref<boolean>(false);
const unwatchSelectedAnnotation = ref<(() => void) | null>(null);
const isGeneratingImage = ref<boolean>(false);
const currentBlobUrl = ref<string | null>(null);

const getAnchoringAnnotation = computed(
  () => store.getters[`${NAMESPACE.ANNOTATION}/getAnchoringAnnotation`],
);
const getAnchoringContextMessage = annotationAdapter.anchoringContextMessage;
const getAnnotationToolbarIds = computed(
  () => store.getters[`${NAMESPACE.ANNOTATION}/getAnnotationToolbarIds`],
);
const isTeacher = computed(() => store.getters[`${NAMESPACE.AUTH}/isTeacher`]);
const isTeacherStudentView = computed(() => annotationContext.isTeacherStudentView);

const selectedAnnotation = computed(() => store.state.annotation.selectedAnnotation);
const currentUserId = computed(() => annotationContext.userId);

const annotatable = computed(() => {
  return (props.canAnnotate || props.canAssess) && canCreateAnnotations.value;
});

const wrapperCssClass = computed(() => {
  return isTeacher.value ? "is-teacher" : "";
});

const cssClass = computed(() => {
  return props.responseSizeHeight;
});

const studentName = computed(() => {
  return annotationContext.getStudentNameById(currentUserId.value);
});

const showDrawingButton = computed(() => {
  return props.isResultMode && isTeacherStudentView.value;
});

const textAnnotationWrapperClass = computed(() => {
  return "text-annotation-wrapper " + props.anchor;
});

const enableAnnotationScroll = computed(() => {
  if (annotatable.value) {
    return true;
  }

  if (annotationContext.contextType === ANNOTATION_CONTEXT_TYPE.EDITOR) {
    return canAccessAnnotations.value;
  }

  if (props.allowAskSalis) {
    return props.isAskSalisSupportedComponent;
  }

  return false;
});

// Watchers
watch(listLoaded, () => {
  if (listLoaded.value && richTextReady.value) {
    loadAnnotation();
  }
});

watch(richTextReady, () => {
  handleRichTextReady();
});

watch(childRichTextReady, () => {
  if (childRichTextReady.value) {
    handleRichTextReady();
  }
});

watch(getAnchoringAnnotation, () => {
  if (richTextReady.value) {
    handleAnchoringToAnnotation();
  }
});

watch(getAnchoringContextMessage, () => {
  if (richTextReady.value) {
    handleAnchoringToContextMessage();
  }
});

function processImageAnnotation(container, parent) {
  const imageSelector = ".sls-image";
  // REVIEW: handling of UGC images?
  const imgAnnotationsLocal: ImageAnnotationPublic[] = [];
  DOMUtils.elOf(container, imageSelector).forEach((imageElement) => {
    const parentElement = DOMUtils.parent(imageElement, ".media-image-view");
    const annotationElement = parentElement
      ? DOMUtils.elOf(parentElement, ".image-annotation")[0]
      : undefined;
    if (!annotationElement) {
      return;
    }

    const imageAnnotationApp = createApp(ImageAnnotation, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      anchor: parent.anchor,
      annotationResourceType: parent.resourceType, // Refer to ANNOTATION_RESOURCE_TYPE for possible values passed as props
      annotationResourceId: parent.annotationResourceId,
      index: imageElement.getAttribute("index"), // Index of MO image inside richtext, used for anchoring
      ownerType: parent.ownerType,
      question:
        typeof parent?.questionContext?.question === "function" &&
        parent.questionContext.question(),
      getMentionableStudentList: parent.getMentionableStudentList,
      getMentionableTeacherList: parent.getMentionableTeacherList,
    });
    AppInstanceUtils.installCommonPlugin(imageAnnotationApp);
    imageAnnotationApp.provide("annotationContext", parent.annotationContext);
    imageAnnotationApp.provide("richTextContext", parent.richTextContext);
    const imgAnnotation = imageAnnotationApp.mount(annotationElement) as ImageAnnotationPublic;
    imgAnnotationsLocal.push(imgAnnotation);
  });
  return imgAnnotationsLocal;
}

function processTextAnnotation(container, parent) {
  const annotationElement = DOMUtils.elOf(
    container,
    ".text-annotation-wrapper." + parent.anchor,
  )[0];
  if (!annotationElement) {
    return null;
  }

  const textAnnotationApp = createApp(TextAnnotation, {
    ...AppInstanceUtils.generateDynamicMountProps(parent),
    anchor: parent.anchor,
    annotationResourceType: parent.resourceType, // Refer to ANNOTATION_RESOURCE_TYPE for possible values passed as props
    annotationResourceId: parent.annotationResourceId,
    canAnnotate: parent.canAnnotate,
    canAssess: parent.canAssess,
    canAskSalisFn: () => {
      return parent.canAskSalis;
    },
    isAskSalisSupportedComponent: parent.isAskSalisSupportedComponent,
    ownerType: parent.ownerType,
    parentComponent: parent,
    containerElement: container,
    question:
      typeof parent?.questionContext?.question === "function" && parent.questionContext.question(),
    getMentionableStudentList: parent.getMentionableStudentList,
    getMentionableTeacherList: parent.getMentionableTeacherList,
  });
  AppInstanceUtils.installCommonPlugin(textAnnotationApp);
  textAnnotationApp.provide("annotationContext", parent.annotationContext);
  textAnnotationApp.provide("richTextContext", parent.richTextContext);
  // Mount media component to media element, include the media tag
  const txtAnnotation = textAnnotationApp.mount(annotationElement);
  if (!txtAnnotation) {
    return null;
  }

  // Keep .text-annotation-wrapper, as replacing it makes subsequent loadAnnotation() calls
  // fail to find the host element for TextAnnotation app,
  // which is needed for unmounting and remounting when the richtext content re-renders.
  // DOMUtils.replaceComponentWrapperWithComponentElement(txtAnnotation);
  return txtAnnotation;
}
function processTimestampAnnotation(container, parent) {
  const mediaSelector = ".sls-player";
  const timestampAnnotations = new Map();

  DOMUtils.elOf(container, mediaSelector).forEach((mediaElement) => {
    const parentElement = DOMUtils.parent(mediaElement, ".media-player");
    const annotationElement = parentElement
      ? DOMUtils.elOf(parentElement, ".timestamp-annotation")[0]
      : undefined;
    if (!annotationElement) {
      return;
    }

    const index = mediaElement.getAttribute("index");
    const timestampAnnotationApp = createApp(TimestampAnnotation, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      anchor: parent.anchor,
      annotationResourceType: parent.resourceType, // Refer to ANNOTATION_RESOURCE_TYPE for possible values passed as props
      annotationResourceId: parent.annotationResourceId,
      index: index, // Index of MO video/audio inside richtext, used for anchoring
      ownerType: parent.ownerType,
      question:
        typeof parent?.questionContext?.question === "function" &&
        parent.questionContext.question(),
      getMentionableStudentList: parent.getMentionableStudentList,
      getMentionableTeacherList: parent.getMentionableTeacherList,
    });
    AppInstanceUtils.installCommonPlugin(timestampAnnotationApp);

    timestampAnnotationApp.provide("annotationContext", parent.annotationContext);
    timestampAnnotationApp.provide("richTextContext", parent.richTextContext);
    const timestampAnnotation = timestampAnnotationApp.mount(annotationElement);

    timestampAnnotations.set(index, timestampAnnotation);
  });
  return timestampAnnotations;
}

const setAnchoringAnnotation = (payload: unknown) =>
  store.dispatch(`${NAMESPACE.ANNOTATION}/setAnchoringAnnotation`, payload);

const setWatchStoreData = () => {
  unwatchSelectedAnnotation.value = store.watch(
    () => store.getters["annotation/getSelectedAnnotation"],
    () => {
      if (
        selectedAnnotation.value.annotation &&
        selectedAnnotation.value.annotation.type === ANNOTATION_TYPE.TOOLTIP_ANNOTATION &&
        selectedAnnotation.value.mode === ANNOTATION_MODE.EDIT &&
        selectedAnnotation.value.annotation.anchor.includes(props.anchor)
      ) {
        handleCollapseDrawer().then(() => {
          handleEditNotes(ANNOTATION_TYPE.TOOLTIP_ANNOTATION, null);
        });
      }
    },
  );
};

const doubleClickImage = (params: unknown) => {
  imgAnnotations.value.forEach((imgAnnotation) => {
    imgAnnotation.handleImgZoomOrReset?.(params);
  });
};

const clickImage = (params: unknown) => {
  imgAnnotations.value.forEach((imgAnnotation) => {
    imgAnnotation.handleClick?.(params);
  });
};

const isModeChanged = (params: unknown) => {
  imgAnnotations.value.forEach((imgAnnotation) => {
    imgAnnotation.isModeChanged?.(params);
  });
};

const timestampTrigger = (params: { index: string }) => {
  if (timestampAnnotations.value.has(params.index)) {
    timestampAnnotations.value.get(params.index)?.handleTrigger(params);
  }
};

const handleSelectionChange = () => {
  if (selectedAnchor.value === props.anchor) {
    // if selected component is the current one
    txtAnnotation.value?.handleSelectionChange(false);
  } else if (getAnnotationToolbarIds.value.includes(props.anchor)) {
    // if selected component is the previous one
    txtAnnotation.value?.handleSelectionChange(true);
  }
};

// Unmounts a dynamically mounted annotation app and drops it from the dynamic-mount tracking
// list, so the onUnmounted teardown (use-dynamic-mount-parent.ts) doesn't try to unmount an
// app that a loadAnnotation() re-run has already replaced.
const unmountTrackedAnnotationApp = (annotationApp: ComponentPublicInstance) => {
  AppInstanceUtils.unmountFromComponent(annotationApp);
  const tracked = dynamicMountParentMixin_childComponents.value;
  const index = tracked.indexOf(annotationApp);
  if (index !== -1) {
    tracked.splice(index, 1);
  }
};

const loadAnnotation = () => {
  const containerEl = container.value;
  if (containerEl) {
    const textAnnotationElement = DOMUtils.elOf(
      containerEl,
      ".text-annotation-wrapper." + props.anchor,
    )[0];
    if (!textAnnotationElement) {
      if (!isTextAnnotationMountRetryPending.value) {
        isTextAnnotationMountRetryPending.value = true;
        nextTick(() => {
          isTextAnnotationMountRetryPending.value = false;
          loadAnnotation();
        });
      }
      return;
    }

    isTextAnnotationMountRetryPending.value = false;
    imgAnnotations.value.forEach(unmountTrackedAnnotationApp);
    timestampAnnotations.value.forEach(unmountTrackedAnnotationApp);
    if (txtAnnotation.value) {
      unmountTrackedAnnotationApp(txtAnnotation.value);
    }

    imgAnnotations.value = processImageAnnotation(containerEl, vm);
    timestampAnnotations.value = processTimestampAnnotation(containerEl, vm);
    txtAnnotation.value = processTextAnnotation(containerEl, vm);
    nextTick(() => {
      if (props.isSERichText && props.parentQuestionContext) {
        questionContext = props.parentQuestionContext;
      }
    });

    if (props.isSERichText && props.parentAnchor) {
      SpeechEvaluationEventBus.$emit("onSERichTextReady", props.parentAnchor);
    }
  }
};

const handleSERichTextReady = (targetAnchor: string) => {
  if (targetAnchor === props.anchor) {
    childRichTextReady.value = true;
  }
};

const handleRichTextReady = () => {
  if ((annotatable.value || listLoaded.value) && richTextReady.value) {
    if (props.hasSEAudio && props.isSEGraded && !childRichTextReady.value) {
      return;
    } else {
      loadAnnotation();
    }

    handleAnchoringToAnnotation();
    handleAnchoringToContextMessage();
  }
};

const handleAnchoringToAnnotation = () => {
  const annotation = getAnchoringAnnotation.value;
  if (annotation !== null && annotation.anchor.includes(props.anchor)) {
    expandAccordion(container.value);
    AnchoringEventBus.$emit("scrollToView", {
      contextType: ANCHORING_CONTEXT_TYPE.ANNOTATION,
      data: getAnchoringAnnotation.value,
    });
    setAnchoringAnnotation(null);
  }
};

const handleAnchoringToContextMessage = async () => {
  const contextMessage = getAnchoringContextMessage.value;
  if (contextMessage !== null && contextMessage.anchor.referenceId.includes(props.anchor)) {
    const requireExpandAccordion = expandAccordion(container.value);

    if (requireExpandAccordion) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    await nextTick(); // add next tick to wait for scenario like content switcher, etc.
    AnchoringEventBus.$emit("scrollToView", {
      contextType: ANCHORING_CONTEXT_TYPE.CONVERSATION_CONTEXT,
      data: getAnchoringContextMessage.value,
    });
    annotationAdapter.setAnchoringContextMessage(null);
  }
};

const onRichTextReady = () => {
  richTextReady.value = true;
  emit("richTextReady");
};

const getSelectionTextAndContainerElement = () => {
  let containerElement: Node | null = null;
  if (typeof window.getSelection != "undefined") {
    const sel = window.getSelection();
    if (sel?.rangeCount) {
      const node = sel?.getRangeAt(0).commonAncestorContainer;
      containerElement = node.nodeType == 1 ? node : node.parentNode;
    }
  }
  const parent = DOMUtils.parent(containerElement, ".annotatable-richtext");
  if (parent) {
    if (props.anchor === parent.id) {
      selectedAnchor.value = parent.id;
    } else if (parent.id.includes(props.anchor)) {
      return;
    }
  }
  handleSelectionChange();
};

const expandAccordion = (element: HTMLElement | null) => {
  if (!element) return false;
  // to check whether is in accordion item
  const accordionItem = DOMUtils.parent(element, ".bx--accordion__content");
  // to check whether the accordion item is active(expanded)
  const isActiveAccordionItem = DOMUtils.parent(element, ".bx--accordion__item--active");

  if (!accordionItem || isActiveAccordionItem) return false;

  // update accordion item state to true(expanded) in store
  const outerAccordionComponent = DOMUtils.parent(element, ".bx--accordion");
  const accordionItemId = accordionItem.id.split("-")[1];
  const outerAccordionComponentId = outerAccordionComponent.id.split("-")[2];
  accordionStore.value.setAccordionItemState(outerAccordionComponentId, accordionItemId, true);
  return true;
};

const handleMarkGoogleFile = (data: unknown) => {
  emit("markGoogleFile", data);
};

const handleMarkCopy = (data: unknown) => {
  emit("markCopy", data);
};

const handleAnchorAnnotation = (index: string) => {
  handleAnchor(index.includes("TOOLTIP_") ? `${container.value?.id}_${index}` : index);
};

const handleMakeImageCopy = async () => {
  isGeneratingImage.value = true;
  try {
    // same as makePdfCopy
    const questionResponses = document.querySelectorAll(".question-response");
    const element = Array.from(questionResponses).find((questionResponse) => {
      return Array.from(questionResponse.children).some((child) => {
        return child.id === props.anchor;
      });
    }) as HTMLElement | undefined;

    if (!element) {
      showImageErrorToast(t("generate_image_error_toast_title"));
      return;
    }
    const targetContent = element.querySelector<HTMLElement>(".rich-text-content");
    if (!targetContent) {
      showImageErrorToast(t("generate_image_error_toast_title"));
      return;
    }

    const imageUrl = await exportRichTextToImage(targetContent);
    if (!imageUrl) {
      return;
    }

    if (currentBlobUrl.value) {
      URL.revokeObjectURL(currentBlobUrl.value);
    }

    currentBlobUrl.value = imageUrl;

    const fileName = studentName.value + "_" + componentContext.label + "(Marked).png";
    const canvasConfig: CanvasConfig = {
      imageUrl: imageUrl,
      uuid: randomUtils.randomUuid(),
      fileName: fileName || "image-copy.png",
      isNewImage: true,
      createAsNew: true,
      skipCropping: true,
    };

    emit("markImage", canvasConfig);
  } catch {
    showImageErrorToast(t("generate_image_error_toast_title"));
  } finally {
    isGeneratingImage.value = false;
  }
};

const showImageErrorToast = (title: string) => {
  isGeneratingImage.value = false;
  toast.show("error", {
    kind: "error",
    title: title,
    subTitle: t("generate_image_error_toast_subtitle"),
  });
};

// Lifecycle hooks
onMounted(() => {
  if (
    selectedAnnotation.value.annotation &&
    selectedAnnotation.value.annotation.type === ANNOTATION_TYPE.TOOLTIP_ANNOTATION &&
    selectedAnnotation.value.mode === ANNOTATION_MODE.EDIT &&
    selectedAnnotation.value.annotation.anchor.includes(props.anchor)
  ) {
    handleCollapseDrawer().then(() => {
      handleEditNotes(ANNOTATION_TYPE.TOOLTIP_ANNOTATION, null);
    });
  }
  setWatchStoreData();

  // REVIEW: annotation, excessive event
  if (annotatable.value || props.allowAskSalis) {
    instance?.appContext.config.globalProperties.$rangy.onSelectionChange(
      getSelectionTextAndContainerElement,
    );
  }

  if (props.hasSEAudio && props.isSEGraded) {
    eventBusWatch.trackWatch(
      SpeechEvaluationEventBus.$on("onSERichTextReady", handleSERichTextReady),
    );
  }

  if (showDrawingButton.value) {
    eventBusWatch.trackWatch(
      drawingEventBus.$on("hide", () => {
        isGeneratingImage.value = false;
      }),
    );
  }
});

onBeforeUnmount(() => {
  instance?.appContext.config.globalProperties.$rangy.offSelectionChange(
    getSelectionTextAndContainerElement,
  );
  unwatchSelectedAnnotation.value?.();

  if (currentBlobUrl.value) {
    URL.revokeObjectURL(currentBlobUrl.value);
    currentBlobUrl.value = null;
  }

  // imgAnnotations.value &&
  //   imgAnnotations.value.forEach(imgAnnotation => {
  //     AppInstanceUtils.unmountFromComponent(imgAnnotation);
  //   });
  // txtAnnotation.value && AppInstanceUtils.unmountFromComponent(txtAnnotation.value);
});

defineExpose({
  dynamicMountParentMixin_childComponents,
});

// Expose to parent
vmExpose({
  // Override mixin methods if applicable
  questionContext: questionContext,
  annotationContext: annotationContext,
  richTextContext: richTextContext,
  getMentionableTeacherList,
  getMentionableStudentList,
  dynamicMountParentMixin_childComponents,
});
</script>

<style lang="scss">
.annotatable-richtext {
  position: relative;
}
</style>
